import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { useAuth } from "../../contexts/AuthProvider";
import {
  IDENTITY_REDIRECT_FLAG,
  clearIdentityRedirectFlag,
  getIdentityLoginUrl,
} from "../../lib/auth/identity";

function safeReturnPath(value: unknown): string {
  return typeof value === "string" && value.startsWith("/") ? value : "/";
}

export default function LoginPage() {
  const { isAuthenticated, isBootstrapping } = useAuth();
  const location = useLocation();
  const [redirectBlocked, setRedirectBlocked] = useState(false);
  const from = safeReturnPath(
    (location.state as { from?: unknown } | null)?.from,
  );
  const returnUrl = useMemo(
    () => new URL(from, window.location.origin).toString(),
    [from],
  );
  const loginUrl = useMemo(() => getIdentityLoginUrl(returnUrl), [returnUrl]);

  useEffect(() => {
    document.title = "Вход в конструктор";
  }, []);

  useEffect(() => {
    if (isAuthenticated || isBootstrapping) return;

    try {
      if (sessionStorage.getItem(IDENTITY_REDIRECT_FLAG)) {
        sessionStorage.removeItem(IDENTITY_REDIRECT_FLAG);
        setRedirectBlocked(true);
        return;
      }
      sessionStorage.setItem(IDENTITY_REDIRECT_FLAG, "1");
    } catch {
      // Продолжаем переход, даже если sessionStorage недоступен.
    }

    window.location.assign(loginUrl);
  }, [isAuthenticated, isBootstrapping, loginUrl]);

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const retryLogin = () => {
    clearIdentityRedirectFlag();
    window.location.assign(loginUrl);
  };

  return (
    <div className="mx-auto flex min-h-[70vh] w-full max-w-md flex-col justify-center px-4 py-12">
      <div className="mb-8 text-center">
        <p className="font-unbounded text-2xl font-extrabold text-ink">
          ЦХМ формы
        </p>
        <h1 className="mt-3 font-onest text-lg font-medium text-slate-700">
          Вход в конструктор
        </h1>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-6 text-center shadow-sm sm:p-8">
        <p className="font-onest text-sm text-slate-600">
          {redirectBlocked
            ? "Сессия не найдена. Войдите через единый аккаунт ЦХМ."
            : "Переходим на страницу входа…"}
        </p>
        {redirectBlocked ? (
          <Button type="button" className="mt-5 w-full" onClick={retryLogin}>
            Войти через ЦХМ ID
          </Button>
        ) : null}
      </div>
    </div>
  );
}
